import classNames from 'classnames'
import ReCAPTCHA from 'react-google-recaptcha'
import React, { FormEvent } from 'react'
import SelectInput from '@components/common/forms/select'
import customStyles from '@components/book-trial-class/trial-booking-component.module.sass'
import styles from '@components/common/dotted-card-bg.module.sass'
import CustomPhoneInput from '@components/book-trial-class/phone-input'
import { childGradeOptions } from '@lib/enums/registration-form'
import { FormBuilder } from '@components/competitions/dynamic-forms'
import Spinner from '@components/common/spinner'
import { RecaptchaType } from '@lib/types/recaptcha'
import { DEFAULT_USER_DETAILS } from '@common/constants'
import { useSessionContext } from '@contexts/session'
import FloatingInput from '@components/common/floating-input'
import Link from 'next/link'
import SelectSchool from '@components/competitions/select-school'

type Props = {
  phone: string
  name: string
  email: string
  role: string
  childName: string
  childGrade: { value: string; label: string }
  childGender: { value: string; label: string }
  loading: boolean
  errors: any
  loggedIn: boolean
  formData: string
  dynamicFormRef: React.MutableRefObject<any>
  recaptchaRef: React.MutableRefObject<RecaptchaType>
  handleCaptcha: (key: string) => void
  handleInputChange: (elem: React.FormEvent<HTMLInputElement> | string) => void
  handlePhoneInputChange: (value: string) => void
  handleSelectChange: (
    id: string,
    obj: {
      value: string
      label: string
    }
  ) => void
  handleSignUpFormSubmit: (e: FormEvent<HTMLFormElement>) => void
  setSchoolName: React.Dispatch<React.SetStateAction<string>>
  setSchoolId: React.Dispatch<React.SetStateAction<number>>
  schoolName: string
}

export const CompetitionRegistrationComponent = (props: Props) => {
  const {
    errors,
    loggedIn,
    name,
    phone,
    handleInputChange,
    childGrade,
    handleSelectChange,
    email,
    handlePhoneInputChange,
    formData,
    dynamicFormRef,
    handleSignUpFormSubmit,
    loading,
    setSchoolName,
    setSchoolId,
    schoolName,
  } = props

  const submitBtnClass = classNames({
    'btn btn-orange w-full py-3 text-lg': true,
    'opacity-50 cursor-not-allowed flex justify-center': loading,
  })

  const { email: currentUserEmail } = useSessionContext()

  return (
    <div
      className={`${styles.dottedCardWrapper} ${customStyles.dottedCardWrapper} w-full sm:w-1/2 my-10 mx-4 flex-1`}
    >
      <form
        className="w-full px-5 py-6 bg-white border border-orange-300 rounded-lg shadow-sm sm:px-10"
        onSubmit={handleSignUpFormSubmit}
        autoComplete="off"
      >
        <ReCAPTCHA
          ref={props.recaptchaRef}
          size="invisible"
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
          onChange={props.handleCaptcha}
        />
        <h1 className="pt-4 text-2xl text-orange font-600">
          Enter your details to complete registration
        </h1>
        <div className="mt-5">
          <FloatingInput
            label="Student's name"
            id="name"
            name="name"
            autoFocus={true}
            value={name}
            onChange={handleInputChange}
            required
            errorText={errors['name']}
          />
        </div>
        <div className="mt-4">
          <CustomPhoneInput
            value={phone}
            onChange={handlePhoneInputChange}
            id="phone"
            name="phone"
            required={true}
            autoFocus={true}
            placeholder="Enter your mobile number"
            errorText={errors['phone']}
            disabled={loggedIn}
          />
        </div>
        <div className="w-full">
          <SelectInput
            id="childGrade"
            placeholder="Select"
            value={childGrade}
            handleSelectChange={handleSelectChange}
            options={childGradeOptions}
            menuPlacement="top"
            errorText={errors['childGrade']}
            required
          />
        </div>
        <div className="mt-4">
          <SelectSchool
            setSchoolName={setSchoolName}
            setSchoolId={setSchoolId}
            schoolName={schoolName}
            errorText={errors['school']}
          />
        </div>
        <div className="mt-4">
          <FloatingInput
            label="Email (optional)"
            id="email"
            name="email"
            type="email"
            value={
              email.includes(DEFAULT_USER_DETAILS.emailDomain) ? '' : email
            }
            onChange={handleInputChange}
            errorText={errors['email']}
            disabled={
              loggedIn &&
              !currentUserEmail.includes(DEFAULT_USER_DETAILS.emailDomain)
            }
          />
        </div>
        <div className="mt-4">
          <FormBuilder
            formData={formData}
            dynamicFormRef={dynamicFormRef}
            isTextInputFloating
          />
        </div>
        <div className="mt-8 mb-5">
          <button className={submitBtnClass} disabled={loading}>
            {loading ? (
              <Spinner classes="text-white h-5 w-5" />
            ) : (
              'Complete registration'
            )}
          </button>
        </div>
        <p className="text-xs text-grey-800">
          By signing up, you agree to the{' '}
          <Link href="/terms/" prefetch={false}>
            <a className="text-blue-200" target="_blank">
              Terms of Service
            </a>
          </Link>{' '}
          and{' '}
          <Link href="/privacy-policy/" prefetch={false}>
            <a className="text-blue-200" target="_blank">
              Privacy Policy
            </a>
          </Link>
          . You also agree that you have parental consent. Important updates
          will be sent via WhatsApp.
        </p>
      </form>
    </div>
  )
}

export default CompetitionRegistrationComponent
